import { showConfirm } from '@/components/Modal/utils'
import { navigateToLoginPage } from '@/utils/navigate'
import { useEffect, useRef } from 'react'

const ACTIVATION_KEYS = new Set(['Enter', ' '])
const INTERACTIVE_TARGET_SELECTOR =
	'a[href], button, input, textarea, select, summary, [contenteditable="true"], [role="button"], [role="link"], [role="menuitem"], [role="textbox"], [tabindex]:not([tabindex="-1"]), [data-anonymous-comment-navigation], [data-anonymous-gated-interaction]'

const isLoginConfirmInteraction = (target: EventTarget | null) => {
	return target instanceof Element && !!target.closest('[role="dialog"]')
}

const isPictureGalleryInteraction = (target: EventTarget | null) => {
	return target instanceof Element && !!target.closest('[data-anonymous-gallery], .yarl__root')
}

const isPermittedInteraction = (target: EventTarget | null) => {
	return isLoginConfirmInteraction(target) || isPictureGalleryInteraction(target)
}

const isFormControl = (target: EventTarget | null): target is HTMLElement => {
	return (
		target instanceof HTMLElement &&
		target.matches('input, textarea, select, [contenteditable="true"], [role="textbox"]')
	)
}

const isInteractiveTarget = (target: EventTarget | null) => {
	return target instanceof Element && !!target.closest(INTERACTIVE_TARGET_SELECTOR)
}

export default function AnonymousInteractionGate() {
	const isConfirmOpenRef = useRef(false)

	useEffect(() => {
		const showLoginConfirm = () => {
			if (isConfirmOpenRef.current) {
				return
			}

			isConfirmOpenRef.current = true
			void showConfirm('登录后即可继续操作', void 0, {
				// showConfirm 的 Promise 仅由“再想想”和“去登录”结算。
				closeOnClickOutside: false,
				closeOnEscape: false,
				confirmProps: {
					children: '去登录',
				},
				onConfirm: navigateToLoginPage,
			})
				.catch(() => undefined)
				.finally(() => {
					isConfirmOpenRef.current = false
				})
		}

		const interceptInteraction = (event: Event) => {
			event.preventDefault()
			event.stopImmediatePropagation()
			showLoginConfirm()
		}

		const handleClick = (event: MouseEvent) => {
			if (isInteractiveTarget(event.target) && !isPermittedInteraction(event.target)) {
				interceptInteraction(event)
			}
		}

		const handleFocusIn = (event: FocusEvent) => {
			const target = event.target

			if (isFormControl(target) && !isPermittedInteraction(target)) {
				interceptInteraction(event)
				target.blur()
			}
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				ACTIVATION_KEYS.has(event.key) &&
				isInteractiveTarget(event.target) &&
				!isPermittedInteraction(event.target)
			) {
				interceptInteraction(event)
			}
		}

		const handleDrop = (event: DragEvent) => {
			if (!isPermittedInteraction(event.target)) {
				interceptInteraction(event)
			}
		}

		document.addEventListener('click', handleClick, true)
		document.addEventListener('auxclick', handleClick, true)
		document.addEventListener('focusin', handleFocusIn, true)
		document.addEventListener('keydown', handleKeyDown, true)
		document.addEventListener('drop', handleDrop, true)

		return () => {
			document.removeEventListener('click', handleClick, true)
			document.removeEventListener('auxclick', handleClick, true)
			document.removeEventListener('focusin', handleFocusIn, true)
			document.removeEventListener('keydown', handleKeyDown, true)
			document.removeEventListener('drop', handleDrop, true)
		}
	}, [])

	return null
}
